/**
 * Level12Scene - 关卡12：时间膨胀
 * @description 教学目标：理解高速运动时时间流逝变慢
 * 核心公式：t' = γt（时间膨胀）
 * 洛伦兹因子：γ = 1/√(1 - v²/c²)
 */

import Phaser from 'phaser';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { Relativity, type RelativityResult } from '@/physics/Relativity.ts';

// 关卡12特定配置
interface Level12SpecificConfig {
  /** 目标地球时间（秒） */
  targetEarthTime: number;
  /** 目标飞船时间范围（最小） */
  targetShipTimeMin: number;
  /** 目标飞船时间范围（最大） */
  targetShipTimeMax: number;
  /** 可选速度选项（以 c 为单位） */
  velocityOptions: number[];
  /** 飞船起始位置 x */
  shipStartX: number;
  /** 飞船起始位置 y */
  shipStartY: number;
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

// 时钟渲染配置
const CLOCK_RADIUS = 60;
const EARTH_CLOCK_X = 150;
const EARTH_CLOCK_Y = 200;
const SHIP_CLOCK_X = 650;
const SHIP_CLOCK_Y = 200;

/**
 * Level12Scene 类
 * 关卡12场景，实现时间膨胀教学
 */
export class Level12Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level12SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;
  private earthTime: number = 0;
  private shipTime: number = 0;

  // 相对论计算器
  private relativity: Relativity;
  private currentVelocity: number = 0;
  private selectedVelocity: number = 0;
  private gamma: number = 1;

  // 游戏对象
  private earthClockGraphics: Phaser.GameObjects.Graphics | null = null;
  private shipClockGraphics: Phaser.GameObjects.Graphics | null = null;
  private spaceshipGraphics: Phaser.GameObjects.Graphics | null = null;
  private trajectoryLine: Phaser.GameObjects.Graphics | null = null;
  private stars: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private velocitySlider: Phaser.GameObjects.Graphics | null = null;
  private velocitySliderHandle: Phaser.GameObjects.Graphics | null = null;
  private velocityButtons: Phaser.GameObjects.Graphics[] = [];
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private gammaDisplay: Phaser.GameObjects.Text | null = null;
  private earthTimeDisplay: Phaser.GameObjects.Text | null = null;
  private shipTimeDisplay: Phaser.GameObjects.Text | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  // 滑块状态
  private sliderDragging: boolean = false;
  private sliderMinX: number = 0;
  private sliderMaxX: number = 0;
  private sliderY: number = 500;

  constructor() {
    super({ key: 'Level12Scene' });
    this.relativity = new Relativity();
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-12', 'src/levels/levels/level-12.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-12') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level12SpecificConfig;
    }

    // 创建背景（太空场景）
    this.createBackground();

    // 创建时钟
    this.createClocks();

    // 创建飞船
    this.createSpaceship();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    // 更新地球时间（正常流逝）
    this.earthTime += delta / 1000;

    // 更新飞船时间（时间膨胀：t' = γt，飞船时间流逝更慢）
    // 注意：这里飞船上的时间流逝变慢，所以 shipTime = earthTime / γ
    // 或者理解为：从地球角度看，飞船上的时钟走得慢
    this.shipTime = this.earthTime / this.gamma;

    // 更新时钟显示
    this.updateClockDisplay();

    // 更新时间显示
    this.updateTimeDisplays();

    // 更新飞船位置（视觉效果）
    this.updateSpaceshipPosition();

    // 绘制轨迹
    this.drawTrajectory();

    // 检测游戏结束
    this.checkGameEnd();
  }

  /**
   * 创建背景（太空场景）
   */
  private createBackground(): void {
    // 深空背景
    this.cameras.main.setBackgroundColor('#0a0a20');

    // 创建星星
    this.stars = this.add.graphics();
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 0.5 + 0.5;

      this.stars.fillStyle(0xffffff, brightness);
      this.stars.fillCircle(x, y, size);
    }

    // 添加星云效果
    const nebula = this.add.graphics();
    nebula.fillStyle(0x4a0080, 0.1);
    nebula.fillCircle(400, 300, 150);
    nebula.fillStyle(0x004080, 0.1);
    nebula.fillCircle(200, 400, 100);
  }

  /**
   * 创建时钟
   */
  private createClocks(): void {
    // 地球时钟
    this.earthClockGraphics = this.add.graphics();
    this.drawClock(this.earthClockGraphics, EARTH_CLOCK_X, EARTH_CLOCK_Y, 0, 0x4ade80, '地球时间');

    // 飞船时钟
    this.shipClockGraphics = this.add.graphics();
    this.drawClock(this.shipClockGraphics, SHIP_CLOCK_X, SHIP_CLOCK_Y, 0, 0xffa500, '飞船时间');

    // 时间数字显示
    this.earthTimeDisplay = this.add.text(EARTH_CLOCK_X, EARTH_CLOCK_Y + CLOCK_RADIUS + 30, '0.00 秒', {
      fontSize: '20px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.shipTimeDisplay = this.add.text(SHIP_CLOCK_X, SHIP_CLOCK_Y + CLOCK_RADIUS + 30, '0.00 秒', {
      fontSize: '20px',
      color: '#ffa500',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  /**
   * 绘制时钟
   */
  private drawClock(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    timeSeconds: number,
    color: number,
    label: string
  ): void {
    graphics.clear();

    // 时钟外圈
    graphics.lineStyle(3, color);
    graphics.strokeCircle(x, y, CLOCK_RADIUS);

    // 时钟内圈
    graphics.fillStyle(0x1a1a3a, 0.8);
    graphics.fillCircle(x, y, CLOCK_RADIUS - 5);

    // 时钟刻度（12个）
    for (let i = 0; i < 12; i++) {
      const angle = (i * 30 - 90) * (Math.PI / 180);
      const innerRadius = CLOCK_RADIUS - 15;
      const outerRadius = CLOCK_RADIUS - 8;

      graphics.lineStyle(2, color);
      graphics.beginPath();
      graphics.moveTo(x + Math.cos(angle) * innerRadius, y + Math.sin(angle) * innerRadius);
      graphics.lineTo(x + Math.cos(angle) * outerRadius, y + Math.sin(angle) * outerRadius);
      graphics.strokePath();
    }

    // 时针（短粗）- 每12秒转一圈
    const hourAngle = ((timeSeconds / 12) * 360 - 90) * (Math.PI / 180);
    graphics.lineStyle(4, color);
    graphics.beginPath();
    graphics.moveTo(x, y);
    graphics.lineTo(x + Math.cos(hourAngle) * (CLOCK_RADIUS * 0.4), y + Math.sin(hourAngle) * (CLOCK_RADIUS * 0.4));
    graphics.strokePath();

    // 分针（长细）- 每60秒转一圈
    const minuteAngle = ((timeSeconds / 60) * 360 - 90) * (Math.PI / 180);
    graphics.lineStyle(2, color);
    graphics.beginPath();
    graphics.moveTo(x, y);
    graphics.lineTo(x + Math.cos(minuteAngle) * (CLOCK_RADIUS * 0.6), y + Math.sin(minuteAngle) * (CLOCK_RADIUS * 0.6));
    graphics.strokePath();

    // 秒针（最长最细）- 60秒转一圈（用于秒级显示）
    const secondAngle = ((timeSeconds % 60) * 6 - 90) * (Math.PI / 180);
    graphics.lineStyle(1, color);
    graphics.beginPath();
    graphics.moveTo(x, y);
    graphics.lineTo(x + Math.cos(secondAngle) * (CLOCK_RADIUS * 0.75), y + Math.sin(secondAngle) * (CLOCK_RADIUS * 0.75));
    graphics.strokePath();

    // 中心点
    graphics.fillStyle(color);
    graphics.fillCircle(x, y, 5);

    // 标签
    this.add.text(x, y - CLOCK_RADIUS - 20, label, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  /**
   * 更新时钟显示
   */
  private updateClockDisplay(): void {
    if (!this.earthClockGraphics || !this.shipClockGraphics) return;

    // 重新绘制时钟
    this.drawClock(this.earthClockGraphics, EARTH_CLOCK_X, EARTH_CLOCK_Y, this.earthTime * 6, 0x4ade80, '地球时间');
    this.drawClock(this.shipClockGraphics, SHIP_CLOCK_X, SHIP_CLOCK_Y, this.shipTime * 6, 0xffa500, '飞船时间');
  }

  /**
   * 更新时间数字显示
   */
  private updateTimeDisplays(): void {
    this.earthTimeDisplay?.setText(`${this.earthTime.toFixed(2)} 秒`);
    this.shipTimeDisplay?.setText(`${this.shipTime.toFixed(2)} 秒`);
  }

  /**
   * 创建飞船
   */
  private createSpaceship(): void {
    this.spaceshipGraphics = this.add.graphics();

    const startX = this.levelSpecific?.shipStartX ?? 100;
    const startY = this.levelSpecific?.shipStartY ?? 400;

    this.drawSpaceship(this.spaceshipGraphics, startX, startY);

    // 飞船标签
    this.add.text(startX, startY + 40, '🚀 飞船', {
      fontSize: '14px',
      color: '#ffa500',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 初始化轨迹线
    this.trajectoryLine = this.add.graphics();
    this.trajectoryLine.setDepth(2);
  }

  /**
   * 绘制飞船
   */
  private drawSpaceship(graphics: Phaser.GameObjects.Graphics, x: number, y: number): void {
    graphics.clear();

    // 飞船主体（三角形）
    graphics.fillStyle(0xffa500, 1);
    graphics.beginPath();
    graphics.moveTo(x + 30, y);
    graphics.lineTo(x - 20, y - 15);
    graphics.lineTo(x - 20, y + 15);
    graphics.closePath();
    graphics.fillPath();

    // 飞船引擎光效（根据速度变化）
    if (this.currentVelocity > 0) {
      const glowIntensity = Math.min(this.currentVelocity, 0.99);
      graphics.fillStyle(0xff6b00, glowIntensity);
      graphics.beginPath();
      graphics.moveTo(x - 20, y - 10);
      graphics.lineTo(x - 20 - 20 * glowIntensity, y);
      graphics.lineTo(x - 20, y + 10);
      graphics.closePath();
      graphics.fillPath();
    }

    // 飞船窗口
    graphics.fillStyle(0x4a90d9, 1);
    graphics.fillCircle(x + 10, y, 5);
  }

  /**
   * 更新飞船位置
   */
  private updateSpaceshipPosition(): void {
    if (!this.spaceshipGraphics) return;

    // 飞船从左向右移动（视觉效果）
    const startX = this.levelSpecific?.shipStartX ?? 100;
    const endX = 700;
    const progress = Math.min(this.earthTime / (this.levelSpecific?.targetEarthTime ?? 10), 1);
    const currentX = startX + (endX - startX) * progress;
    const y = this.levelSpecific?.shipStartY ?? 400;

    this.drawSpaceship(this.spaceshipGraphics, currentX, y);
  }

  /**
   * 绘制轨迹
   */
  private drawTrajectory(): void {
    if (!this.trajectoryLine || !this.spaceshipGraphics) return;

    const startX = this.levelSpecific?.shipStartX ?? 100;
    const endX = 700;
    const progress = Math.min(this.earthTime / (this.levelSpecific?.targetEarthTime ?? 10), 1);
    const currentX = startX + (endX - startX) * progress;
    const y = this.levelSpecific?.shipStartY ?? 400;

    // 绘制轨迹点
    const color = this.gamma > 1.5 ? 0xff6b00 : 0xffa500;
    this.trajectoryLine.fillStyle(color, 0.3);
    this.trajectoryLine.fillCircle(currentX, y, 2);
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 公式显示
    this.add.text(GAME_WIDTH / 2, 30, '时间膨胀公式：t\' = γt', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 洛伦兹因子显示
    this.gammaDisplay = this.add.text(GAME_WIDTH / 2, 60, 'γ = 1/√(1 - v²/c²) = 1.00', {
      fontSize: '16px',
      color: '#6b8cff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      85,
      this.levelSpecific?.hint ?? '调节飞船速度，观察时间膨胀效应',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 目标区域显示
    this.createTargetDisplay();

    // 速度控制
    this.createVelocityControls();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, '', {
      fontSize: '28px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      backgroundColor: '#1a1a3a',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setVisible(false).setDepth(10);

    // 物理说明区域
    this.createPhysicsInfo();
  }

  /**
   * 创建目标显示
   */
  private createTargetDisplay(): void {
    if (!this.levelSpecific) return;

    // 目标时间显示框
    const targetBox = this.add.graphics();
    targetBox.fillStyle(0x2a2a4a, 0.8);
    targetBox.fillRect(GAME_WIDTH / 2 - 150, 350, 300, 80);
    targetBox.lineStyle(2, 0x4ade80);
    targetBox.strokeRect(GAME_WIDTH / 2 - 150, 350, 300, 80);

    this.add.text(GAME_WIDTH / 2, 370, '🎯 目标', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      400,
      `当地球过 ${this.levelSpecific.targetEarthTime} 秒时，飞船时间应在 ${this.levelSpecific.targetShipTimeMin}-${this.levelSpecific.targetShipTimeMax} 秒`,
      {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
  }

  /**
   * 创建速度控制
   */
  private createVelocityControls(): void {
    if (!this.levelSpecific) return;

    // 速度选项标签
    this.add.text(GAME_WIDTH / 2, 450, '选择飞船速度（以光速 c 为单位）：', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 速度选项按钮
    const velocities = this.levelSpecific.velocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 470;

    velocities.forEach((velocity, i) => {
      const x = startX + i * 80 + 40;
      const btn = this.add.graphics();
      const isSelected = this.selectedVelocity === velocity;

      // 按钮样式
      const color = this.getVelocityColor(velocity);
      btn.fillStyle(isSelected ? color : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRect(x - 35, y, 70, 35);
      btn.lineStyle(2, isSelected ? color : 0x6a6a8a);
      btn.strokeRect(x - 35, y, 70, 35);

      // 速度文本
      this.add.text(x, y + 17, `${velocity}c`, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      // 交互
      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 35, y, 70, 35),
        Phaser.Geom.Rectangle.Contains
      );

      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.selectedVelocity = velocity;
          this.currentVelocity = velocity;
          this.updateGammaDisplay();
          this.updateVelocityButtons();
          this.updateHint();
        }
      });

      btn.on('pointerover', () => {
        if (this.gameState === 'idle') {
          btn.clear();
          btn.fillStyle(this.getVelocityColor(velocity), 0.9);
          btn.fillRect(x - 35, y, 70, 35);
          btn.strokeRect(x - 35, y, 70, 35);
        }
      });

      btn.on('pointerout', () => {
        if (this.gameState === 'idle') {
          this.updateVelocityButtons();
        }
      });

      this.velocityButtons.push(btn);
    });

    // 创建速度滑块（额外控制）
    this.createVelocitySlider();
  }

  /**
   * 根据速度返回颜色
   */
  private getVelocityColor(velocity: number): number {
    if (velocity < 0.3) return 0x4ade80; // 低速：绿色
    if (velocity < 0.7) return 0xffa500; // 中速：橙色
    return 0xff6b6b; // 高速：红色
  }

  /**
   * 创建速度滑块
   */
  private createVelocitySlider(): void {
    // 滑轨
    this.sliderMinX = 100;
    this.sliderMaxX = GAME_WIDTH - 100;
    this.sliderY = 540;

    this.velocitySlider = this.add.graphics();
    this.velocitySlider.fillStyle(0x4a4a6a, 1);
    this.velocitySlider.fillRect(this.sliderMinX, this.sliderY - 5, this.sliderMaxX - this.sliderMinX, 10);
    this.velocitySlider.lineStyle(1, 0x6a6a8a);
    this.velocitySlider.strokeRect(this.sliderMinX, this.sliderY - 5, this.sliderMaxX - this.sliderMinX, 10);

    // 滑块手柄
    this.velocitySliderHandle = this.add.graphics();
    this.updateSliderHandle(0);

    // 滑块标签
    this.add.text(this.sliderMinX, this.sliderY + 15, '0c', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(this.sliderMaxX, this.sliderY + 15, '0.99c', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 滑块交互区域
    const sliderZone = this.add.graphics();
    sliderZone.fillStyle(0xffffff, 0);
    sliderZone.fillRect(this.sliderMinX, this.sliderY - 20, this.sliderMaxX - this.sliderMinX, 40);
    sliderZone.setInteractive(
      new Phaser.Geom.Rectangle(this.sliderMinX, this.sliderY - 20, this.sliderMaxX - this.sliderMinX, 40),
      Phaser.Geom.Rectangle.Contains
    );

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.gameState === 'idle' && pointer.y >= this.sliderY - 20 && pointer.y <= this.sliderY + 20) {
        this.sliderDragging = true;
        this.updateSliderFromPointer(pointer.x);
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.sliderDragging && this.gameState === 'idle') {
        this.updateSliderFromPointer(pointer.x);
      }
    });

    this.input.on('pointerup', () => {
      this.sliderDragging = false;
    });
  }

  /**
   * 从指针位置更新滑块
   */
  private updateSliderFromPointer(pointerX: number): void {
    const clampedX = Math.max(this.sliderMinX, Math.min(this.sliderMaxX, pointerX));
    const velocity = (clampedX - this.sliderMinX) / (this.sliderMaxX - this.sliderMinX) * 0.99;

    // 使用滑块时清除按钮选择
    this.selectedVelocity = 0;
    this.currentVelocity = velocity;
    this.updateSliderHandle(velocity);
    this.updateGammaDisplay();
    this.updateVelocityButtons();
    this.updateHint();
  }

  /**
   * 更新滑块手柄位置
   */
  private updateSliderHandle(velocity: number): void {
    if (!this.velocitySliderHandle) return;

    const handleX = this.sliderMinX + (velocity / 0.99) * (this.sliderMaxX - this.sliderMinX);
    const color = this.getVelocityColor(velocity);

    this.velocitySliderHandle.clear();
    this.velocitySliderHandle.fillStyle(color, 1);
    this.velocitySliderHandle.fillCircle(handleX, this.sliderY, 12);
    this.velocitySliderHandle.lineStyle(2, 0xffffff);
    this.velocitySliderHandle.strokeCircle(handleX, this.sliderY, 12);
  }

  /**
   * 更新速度按钮状态
   */
  private updateVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.velocityOptions;
    const startX = GAME_WIDTH / 2 - (velocities.length * 80) / 2;
    const y = 470;

    this.velocityButtons.forEach((btn, i) => {
      const velocity = velocities[i];
      if (velocity === undefined) return;
      const x = startX + i * 80 + 40;
      const isSelected = this.selectedVelocity === velocity;
      const color = this.getVelocityColor(velocity);

      btn.clear();
      btn.fillStyle(isSelected ? color : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRect(x - 35, y, 70, 35);
      btn.lineStyle(2, isSelected ? color : 0x6a6a8a);
      btn.strokeRect(x - 35, y, 70, 35);
    });
  }

  /**
   * 更新洛伦兹因子显示
   */
  private updateGammaDisplay(): void {
    if (this.currentVelocity === 0) {
      this.gamma = 1;
      this.gammaDisplay?.setText('γ = 1/√(1 - v²/c²) = 1.00');
      return;
    }

    try {
      const result: RelativityResult = this.relativity.timeDilation(1, this.currentVelocity);
      this.gamma = result.gamma;
      this.gammaDisplay?.setText(`γ = 1/√(1 - ${(this.currentVelocity).toFixed(2)}²) = ${this.gamma.toFixed(3)}`);
    } catch {
      this.gammaDisplay?.setText('γ = ∞（速度过快）');
    }
  }

  /**
   * 更新提示文本
   */
  private updateHint(): void {
    if (this.currentVelocity === 0) {
      this.hintText?.setText('请选择飞船速度！');
      return;
    }

    const timeRatio = this.gamma.toFixed(2);
    const speedLevel = this.relativity.getSpeedLevel(this.currentVelocity);
    let hintMessage = '';

    switch (speedLevel) {
      case 'low':
        hintMessage = `低速飞行（${this.currentVelocity.toFixed(2)}c），时间膨胀效应轻微（γ≈${timeRatio})`;
        break;
      case 'medium':
        hintMessage = `中速飞行（${this.currentVelocity.toFixed(2)}c），时间膨胀开始显现（γ=${timeRatio})`;
        break;
      case 'high':
        hintMessage = `高速飞行（${this.currentVelocity.toFixed(2)}c），时间膨胀效应显著（γ=${timeRatio})！`;
        break;
      case 'ultra':
        hintMessage = `接近光速（${this.currentVelocity.toFixed(2)}c），时间几乎静止（γ=${timeRatio})！`;
        break;
    }

    this.hintText?.setText(hintMessage);
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(GAME_WIDTH - 140, 540, 120, 40);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(GAME_WIDTH - 140, 540, 120, 40);

    this.add.text(GAME_WIDTH - 80, 560, '发射', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH - 140, 540, 120, 40),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      if (this.gameState === 'idle') {
        this.startButton?.clear();
        this.startButton?.fillStyle(0x5aee90, 1);
        this.startButton?.fillRect(GAME_WIDTH - 140, 540, 120, 40);
        this.startButton?.strokeRect(GAME_WIDTH - 140, 540, 120, 40);
      }
    });

    this.startButton.on('pointerout', () => {
      if (this.gameState === 'idle') {
        this.startButton?.clear();
        this.startButton?.fillStyle(0x4ade80, 1);
        this.startButton?.fillRect(GAME_WIDTH - 140, 540, 120, 40);
        this.startButton?.strokeRect(GAME_WIDTH - 140, 540, 120, 40);
      }
    });
  }

  /**
   * 创建物理信息显示
   */
  private createPhysicsInfo(): void {
    // 时间膨胀公式说明
    const infoBox = this.add.graphics();
    infoBox.fillStyle(0x2a2a4a, 0.6);
    infoBox.fillRect(50, GAME_HEIGHT - 80, 700, 70);

    this.add.text(70, GAME_HEIGHT - 65, '💡 物理原理', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    this.add.text(70, GAME_HEIGHT - 45, '时间膨胀：飞船高速运动时，飞船上的时间流逝变慢', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    this.add.text(70, GAME_HEIGHT - 25, '公式：t\' = γt，其中 γ = 1/√(1 - v²/c²)，速度越快 γ 越大，时间膨胀效应越明显', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }
    });
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;
    if (this.currentVelocity === 0) {
      this.hintText?.setText('请先选择飞船速度！');
      return;
    }

    this.gameState = 'running';
    this.elapsedTime = 0;
    this.earthTime = 0;
    this.shipTime = 0;

    // 计算洛伦兹因子
    this.updateGammaDisplay();

    // 清除轨迹
    if (this.trajectoryLine) {
      this.trajectoryLine.clear();
    }

    // 重置时钟
    this.updateClockDisplay();
    this.updateTimeDisplays();

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更新开始按钮状态
    this.updateStartButtonState('running');

    // 更新提示
    this.hintText?.setText(`飞船以 ${this.currentVelocity.toFixed(2)}c 速度飞行，观察时间差异...`);
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
    } else {
      this.startButton.fillStyle(0x4ade80, 1);
    }
    this.startButton.fillRect(GAME_WIDTH - 140, 540, 120, 40);
    this.startButton.strokeRect(GAME_WIDTH - 140, 540, 120, 40);
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    if (!this.levelSpecific) return;

    const targetEarthTime = this.levelSpecific.targetEarthTime;
    const targetShipTimeMin = this.levelSpecific.targetShipTimeMin;
    const targetShipTimeMax = this.levelSpecific.targetShipTimeMax;

    // 检测是否到达目标地球时间
    if (this.earthTime >= targetEarthTime) {
      // 检测飞船时间是否在目标范围内
      if (this.shipTime >= targetShipTimeMin && this.shipTime <= targetShipTimeMax) {
        this.gameState = 'success';
        this.showSuccessResult();
      } else {
        this.gameState = 'failed';
        this.showFailResult('飞船时间不在目标范围内！');
      }

      this.updateStartButtonState('idle');
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(12, this.elapsedTime);

    this.resultText?.setText(`🎉 成功！\n地球时间：${this.earthTime.toFixed(2)}秒\n飞船时间：${this.shipTime.toFixed(2)}秒\n洛伦兹因子：γ=${this.gamma.toFixed(3)}`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(`时间膨胀效应正确！飞船速度 ${this.currentVelocity.toFixed(2)}c 使时间膨胀了 ${this.gamma.toFixed(3)} 倍`);

    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 80;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(13)) {
        this.scene.start('Level13Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2, y + 40, '重新尝试', {
      fontSize: '16px',
      color: '#ffa500',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetGame();
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}\n地球时间：${this.earthTime.toFixed(2)}秒\n飞船时间：${this.shipTime.toFixed(2)}秒\n目标：${this.levelSpecific?.targetShipTimeMin}-${this.levelSpecific?.targetShipTimeMax}秒`)
      .setColor('#ff6b6b')
      .setVisible(true);

    // 根据失败原因给出提示
    if (this.shipTime > (this.levelSpecific?.targetShipTimeMax ?? 0)) {
      this.hintText?.setText('提示：飞船时间过长，说明速度不够快，时间膨胀效应不足。选择更高的速度！');
    } else if (this.shipTime < (this.levelSpecific?.targetShipTimeMin ?? 0)) {
      this.hintText?.setText('提示：飞船时间过短，说明速度太快，时间膨胀过度。选择较低的速度！');
    }
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.earthTime = 0;
    this.shipTime = 0;

    // 清除轨迹
    if (this.trajectoryLine) {
      this.trajectoryLine.clear();
    }

    // 重置时钟
    this.updateClockDisplay();
    this.updateTimeDisplays();

    // 重置飞船位置
    if (this.spaceshipGraphics) {
      this.drawSpaceship(this.spaceshipGraphics, this.levelSpecific?.shipStartX ?? 100, this.levelSpecific?.shipStartY ?? 400);
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '调节飞船速度，观察时间膨胀效应');

    // 更新按钮状态
    this.updateStartButtonState('idle');
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-12');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 12,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取地球时间
   */
  getEarthTime(): number {
    return this.earthTime;
  }

  /**
   * 获取飞船时间
   */
  getShipTime(): number {
    return this.shipTime;
  }

  /**
   * 获取当前速度
   */
  getCurrentVelocity(): number {
    return this.currentVelocity;
  }

  /**
   * 获取洛伦兹因子
   */
  getGamma(): number {
    return this.gamma;
  }
}